import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { useSnackbar } from 'notistack';
import tesoreriaCajaService, {
    ImportacionMediosCobroFilaMensaje,
    ImportacionMediosCobroResult,
} from '../../services/tesoreriaCajaService';
import { GrupoCajaChica } from '../../services/tesoreriaService';
import { downloadBlob } from '../../utils/downloadBlob';
import { formatCurrency } from '../../utils/formatters';

interface ImportarMediosCobroDialogProps {
    open: boolean;
    onClose: () => void;
    onImportado: (resultado: ImportacionMediosCobroResult) => void;
    nroSuc: number;
    codigoEntidad: number;
    conceptoLabel?: string;
    fecha?: string;
    descripcion?: string;
    grupoCaja?: GrupoCajaChica | null;
    gruposCaja?: GrupoCajaChica[];
    hayPagosCargados?: boolean;
}

const CAJA_ULTIMA_ABIERTA = '__ultima__';
const CAJA_SEGUN_MCP = '__mcp__';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const extraerMensajeError = (error: any, porDefecto: string): string => {
    const data = error?.response?.data;
    if (!data) return porDefecto;
    if (typeof data === 'string') return data;
    if (data.errors && typeof data.errors === 'object') {
        const primera = Object.keys(data.errors)[0];
        const msg = (data.errors as Record<string, string[]>)[primera]?.[0];
        if (msg) return `${data.message ? `${data.message} ` : ''}${msg}`;
    }
    return data.message || data.title || porDefecto;
};

const TablaMensajes: React.FC<{ titulo: string; items: ImportacionMediosCobroFilaMensaje[]; color: 'error' | 'warning' }> = ({
    titulo,
    items,
    color,
}) => (
    <Box>
        <Typography variant="subtitle2" color={`${color}.main`} sx={{ mb: 0.5 }}>
            {titulo} ({items.length})
        </Typography>
        <TableContainer sx={{ maxHeight: 220, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 70 }}>Fila</TableCell>
                        <TableCell sx={{ width: 140 }}>Columna</TableCell>
                        <TableCell>Mensaje</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((e, i) => (
                        <TableRow key={`${e.fila}-${e.columna}-${i}`}>
                            <TableCell>{e.fila > 0 ? e.fila : 'General'}</TableCell>
                            <TableCell>{e.columna || '-'}</TableCell>
                            <TableCell>{e.mensaje}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

const ImportarMediosCobroDialog: React.FC<ImportarMediosCobroDialogProps> = ({
    open,
    onClose,
    onImportado,
    nroSuc,
    codigoEntidad,
    conceptoLabel,
    fecha,
    descripcion,
    grupoCaja,
    gruposCaja = [],
    hayPagosCargados = false,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [archivo, setArchivo] = useState<File | null>(null);
    const [cajaDestino, setCajaDestino] = useState<string>(CAJA_ULTIMA_ABIERTA);
    const [omitirDuplicados, setOmitirDuplicados] = useState(false);
    const [resultado, setResultado] = useState<ImportacionMediosCobroResult | null>(null);
    const [validando, setValidando] = useState(false);
    const [importando, setImportando] = useState(false);
    const [descargando, setDescargando] = useState(false);

    useEffect(() => {
        if (open) {
            setArchivo(null);
            setResultado(null);
            setOmitirDuplicados(false);
            setCajaDestino(CAJA_ULTIMA_ABIERTA);
        }
    }, [open]);

    const agruparPorMcp = cajaDestino === CAJA_SEGUN_MCP;
    const grupoMCP = cajaDestino === CAJA_ULTIMA_ABIERTA || agruparPorMcp ? null : Number(cajaDestino);

    const opcionesCaja = useMemo(() => {
        const lista = [...gruposCaja];
        if (grupoCaja && !lista.some((g) => g.codigo === grupoCaja.codigo)) lista.unshift(grupoCaja);
        return lista;
    }, [gruposCaja, grupoCaja]);

    const nombreGrupo = useMemo(() => {
        const map = new Map<number, string>();
        gruposCaja.forEach((g) => map.set(g.codigo, g.nombre));
        if (grupoCaja) map.set(grupoCaja.codigo, grupoCaja.nombre);
        return (codigo: number) => (map.get(codigo) ? `${map.get(codigo)} (${codigo})` : `Grupo ${codigo}`);
    }, [gruposCaja, grupoCaja]);

    const validacionOk = !!resultado && resultado.dryRun && resultado.errores.length === 0 && resultado.filasValidas > 0;
    const ocupado = validando || importando;

    const handleDescargarPlantilla = async () => {
        try {
            setDescargando(true);
            const blob = await tesoreriaCajaService.descargarPlantillaImportacionMediosCobro();
            downloadBlob(blob, 'Plantilla_Importacion_MediosCobro.xlsx', XLSX_MIME);
        } catch (error) {
            enqueueSnackbar(extraerMensajeError(error, 'No se pudo descargar la plantilla'), { variant: 'error' });
        } finally {
            setDescargando(false);
        }
    };

    const handleArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (file && !/\.(xlsx|xlsm)$/i.test(file.name)) {
            enqueueSnackbar('Solo se admiten archivos .xlsx o .xlsm', { variant: 'warning' });
            return;
        }
        setArchivo(file);
        setResultado(null);
    };

    const ejecutar = async (dryRun: boolean) => {
        if (!archivo) {
            enqueueSnackbar('Seleccioná un archivo Excel', { variant: 'warning' });
            return;
        }
        const setLoading = dryRun ? setValidando : setImportando;
        try {
            setLoading(true);
            const res = await tesoreriaCajaService.importarMediosCobro({
                archivo,
                nroSuc,
                codigoEntidad,
                grupoMCP,
                fecha: fecha || null,
                descripcion: descripcion || null,
                dryRun,
                omitirDuplicados,
                agruparPorMcp,
            });
            setResultado(res);
            if (!dryRun) {
                if (res.importado) {
                    const comprobantes = res.movimientos
                        .filter((m) => m.estado === 'Creado')
                        .map((m) => m.nroComprobante ?? m.nroFormulario)
                        .join(', ');
                    enqueueSnackbar(
                        `Importación exitosa: ${res.filasValidas} medios por ${formatCurrency(res.importeTotal)}. Comprobantes: ${comprobantes}`,
                        { variant: 'success', autoHideDuration: 8000 },
                    );
                    onImportado(res);
                } else {
                    enqueueSnackbar('La importación no se completó. Revisá el detalle.', { variant: 'error' });
                }
            } else if (res.errores.length > 0) {
                enqueueSnackbar(`El archivo tiene ${res.errores.length} error(es). Corregilos y volvé a validar.`, { variant: 'warning' });
            }
        } catch (error) {
            enqueueSnackbar(extraerMensajeError(error, dryRun ? 'Error al validar el archivo' : 'Error al importar'), {
                variant: 'error',
                autoHideDuration: 8000,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={ocupado ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>Importar medios de cobro desde Excel</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Cada fila del Excel es un medio de cobro: cheques de terceros, efectivo, transferencias u otros.
                        Se genera un Ingreso Varios por cada caja, con el concepto y la sucursal de esta pantalla.
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`Sucursal ${nroSuc}`} />
                        <Chip size="small" label={`Concepto: ${conceptoLabel || codigoEntidad}`} />
                        {fecha && <Chip size="small" label={`Fecha: ${fecha.split('-').reverse().join('/')}`} />}
                    </Stack>

                    {hayPagosCargados && (
                        <Alert severity="warning">
                            Los medios de pago agregados manualmente en esta pantalla no se incluyen en la importación.
                        </Alert>
                    )}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                        <Button
                            variant="outlined"
                            startIcon={descargando ? <CircularProgress size={16} /> : <DownloadIcon />}
                            onClick={handleDescargarPlantilla}
                            disabled={descargando}
                            sx={{ textTransform: 'none' }}
                        >
                            Descargar plantilla
                        </Button>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileIcon />}
                            disabled={ocupado}
                            sx={{ textTransform: 'none' }}
                        >
                            Seleccionar archivo
                            <input type="file" hidden accept=".xlsx,.xlsm" onChange={handleArchivo} />
                        </Button>
                        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                            {archivo ? archivo.name : 'Ningún archivo seleccionado'}
                        </Typography>
                    </Stack>

                    <Box>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Caja destino"
                            value={cajaDestino}
                            onChange={(e) => {
                                setCajaDestino(e.target.value);
                                setResultado(null);
                            }}
                            disabled={ocupado}
                            helperText={
                                agruparPorMcp
                                    ? 'Cada medio irá a la caja de su MCP (puede generar más de un movimiento).'
                                    : cajaDestino === CAJA_ULTIMA_ABIERTA
                                      ? 'Se usa la caja abierta más reciente (por fecha de apertura) de las cajas asignadas al usuario. Validá para ver cuál es.'
                                      : 'Todos los medios se imputan a esta caja en un único movimiento.'
                            }
                            sx={{ mb: 1 }}
                        >
                            <MenuItem value={CAJA_ULTIMA_ABIERTA}>Última caja abierta (automático)</MenuItem>
                            <MenuItem value={CAJA_SEGUN_MCP}>Según la caja del MCP de cada medio</MenuItem>
                            {opcionesCaja.map((g) => (
                                <MenuItem key={g.codigo} value={String(g.codigo)}>
                                    {g.nombre} ({g.codigo})
                                </MenuItem>
                            ))}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={omitirDuplicados}
                                    onChange={(e) => {
                                        setOmitirDuplicados(e.target.checked);
                                        setResultado(null);
                                    }}
                                    disabled={ocupado}
                                />
                            }
                            label="Omitir cheques ya cargados en lugar de rechazar la importación"
                        />
                    </Box>

                    {resultado && (
                        <Stack spacing={2}>
                            <Alert
                                severity={
                                    resultado.errores.length > 0 ? 'error' : resultado.importado ? 'success' : 'info'
                                }
                            >
                                {resultado.dryRun
                                    ? resultado.errores.length > 0
                                        ? 'El archivo tiene errores. No se puede importar hasta corregirlos.'
                                        : 'Validación correcta. Revisá debajo los totales, los movimientos a generar y las advertencias; si todo coincide, presioná Importar.'
                                    : resultado.importado
                                        ? 'Importación finalizada correctamente.'
                                        : 'La importación no se completó.'}
                            </Alert>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip size="small" label={`Filas leídas: ${resultado.filasLeidas}`} />
                                <Chip size="small" color="success" variant="outlined" label={`Válidas: ${resultado.filasValidas}`} />
                                {resultado.filasConError > 0 && (
                                    <Chip size="small" color="error" variant="outlined" label={`Con error: ${resultado.filasConError}`} />
                                )}
                                {resultado.filasOmitidas > 0 && (
                                    <Chip size="small" color="warning" variant="outlined" label={`Omitidas: ${resultado.filasOmitidas}`} />
                                )}
                                <Chip size="small" label={`Cheques: ${resultado.cantidadCheques}`} />
                                <Chip size="small" color="primary" label={`Total: ${formatCurrency(resultado.importeTotal)}`} />
                            </Stack>

                            {resultado.grupoMCPDestino != null && (
                                <Alert severity="info" variant="outlined">
                                    Caja destino{resultado.modoCaja === 'UltimaCajaAbierta' ? ' (última abierta)' : ''}:{' '}
                                    <strong>
                                        {resultado.descripcionCajaDestino || `Grupo ${resultado.grupoMCPDestino}`}
                                        {resultado.codigoCajaDestino != null ? ` — caja ${resultado.codigoCajaDestino}` : ''}
                                    </strong>
                                    {resultado.fechaAperturaCajaDestino
                                        ? `, abierta el ${new Date(resultado.fechaAperturaCajaDestino).toLocaleString('es-AR')}`
                                        : ''}
                                </Alert>
                            )}

                            {resultado.movimientos.length > 0 && (
                                <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Caja</TableCell>
                                                <TableCell align="right">Medios</TableCell>
                                                <TableCell align="right">Cheques</TableCell>
                                                <TableCell align="right">Importe</TableCell>
                                                <TableCell>Estado</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {resultado.movimientos.map((m) => (
                                                <TableRow key={m.grupoMCP}>
                                                    <TableCell>{nombreGrupo(m.grupoMCP)}</TableCell>
                                                    <TableCell align="right">{m.cantidadMedios}</TableCell>
                                                    <TableCell align="right">{m.cantidadCheques}</TableCell>
                                                    <TableCell align="right">{formatCurrency(m.importeTotal)}</TableCell>
                                                    <TableCell>
                                                        {m.estado === 'Creado'
                                                            ? `Creado (comprobante ${m.nroComprobante ?? m.nroFormulario})`
                                                            : m.estado === 'Pendiente'
                                                                ? 'A generar'
                                                                : m.estado === 'NoProcesado'
                                                                    ? 'No procesado'
                                                                    : `Error: ${m.error ?? ''}`}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {resultado.errores.length > 0 && (
                                <TablaMensajes titulo="Errores" items={resultado.errores} color="error" />
                            )}
                            {resultado.advertencias.length > 0 && (
                                <TablaMensajes titulo="Advertencias" items={resultado.advertencias} color="warning" />
                            )}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={ocupado} sx={{ textTransform: 'none' }}>
                    {resultado?.importado ? 'Cerrar' : 'Cancelar'}
                </Button>
                <Button
                    variant="outlined"
                    startIcon={validando ? <CircularProgress size={16} /> : <FactCheckIcon />}
                    onClick={() => ejecutar(true)}
                    disabled={!archivo || ocupado || !!resultado?.importado}
                    sx={{ textTransform: 'none' }}
                >
                    Validar
                </Button>
                <Button
                    variant="contained"
                    startIcon={importando ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                    onClick={() => ejecutar(false)}
                    disabled={!validacionOk || ocupado}
                    sx={{ textTransform: 'none' }}
                >
                    Importar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImportarMediosCobroDialog;
